/**
 * <notify-popup> — out-of-stock email signup popup.
 *
 * Open/close, focus trap, body scroll lock and overlay are owned by the shared
 * popup singleton (@theme/popup): the trigger button uses
 * data-action="toggle-popup" + aria-controls, the close button uses
 * data-action="close-modal". This element only owns the Klaviyo signup, the
 * variant image swap, and the success/error content state.
 *
 * Submitting does two things, in this order: it writes the market the request was made from
 * onto the profile, then creates the back-in-stock subscription for the variant. Both use the
 * PUBLIC company id — never a private API key.
 *
 * Two calls because the subscription endpoint rejects profile properties outright
 * ("'properties' is not a valid field for the resource 'profile'"), while the flow needs to
 * know which storefront the visitor was on to send the right language.
 */

import { globalPopup } from '@theme/popup';

class NotifyPopup extends HTMLElement {
  #host = null;
  #imageEl = null;
  #form = null;
  #statusEl = null;
  #submitBtn = null;
  #submitLabel = null;
  #titleEl = null;
  #initialTitle = '';
  #initialImageSrc = '';
  #currentVariantId = '';
  #abortController = null;

  connectedCallback() {
    this.#host = this.closest('main-product');
    this.#imageEl = this.querySelector('[data-notify-image]');
    this.#form = this.querySelector('[data-notify-form]');
    this.#statusEl = this.querySelector('[data-notify-status]');
    this.#submitBtn = this.querySelector('[data-notify-submit]');
    this.#submitLabel = this.querySelector('[data-notify-submit-label]');
    this.#titleEl = this.querySelector('.notify-popup__title');
    this.#initialTitle = this.#titleEl?.textContent.trim() || '';
    // What Liquid rendered: the product's own image, kept as the fallback for a variant
    // that carries none of its own.
    this.#initialImageSrc = this.#imageEl?.getAttribute('src') || '';
    this.#currentVariantId = this.#host?.querySelector('[data-atc-variant-id]')?.value || '';

    this.addEventListener('click', this.#onClick);
    this.addEventListener('popup:opened', this.#onOpened);
    this.addEventListener('popup:closed', this.#onClosed);
    this.#host?.addEventListener('pdp:variant-update', this.#onVariantUpdate);
    this.#form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('popup:opened', this.#onOpened);
    this.removeEventListener('popup:closed', this.#onClosed);
    this.#host?.removeEventListener('pdp:variant-update', this.#onVariantUpdate);
    this.#form?.removeEventListener('submit', this.#onSubmit);
    this.#abortController?.abort();
  }

  #onClick = (event) => {
    if (event.target.closest('[data-action="close-modal"]')) {
      event.preventDefault();
      globalPopup.closePopup(this);
    }
  };

  #onOpened = () => {
    this.querySelector('input[type="email"]')?.focus();
  };

  #onClosed = () => {
    this.#hideStatus();
    this.#resetState();
  };

  #onVariantUpdate = (event) => {
    const variant = event.detail?.variant;
    if (!variant) return;
    if (variant.id) this.#currentVariantId = String(variant.id);
    if (!this.#imageEl) return;

    // The variants JSON in sections/main-product.liquid renders featured_image as a plain
    // URL string (`variant.featured_image | image_url`), not as Shopify's image object, so
    // reading `.src` off it was always undefined and the image never changed - the popup
    // kept whichever variant Liquid had rendered. An object is still accepted in case the
    // payload ever carries one.
    const image = variant.featured_image;
    const src = typeof image === 'string' ? image : image?.src;

    // A variant with no image of its own falls back to the product image rather than
    // keeping the one before it - showing another colour is worse than showing none.
    this.#imageEl.src = src || this.#initialImageSrc;
  };

  #onSubmit = async (event) => {
    event.preventDefault();
    if (!this.#form) return;

    const emailInput = this.#form.querySelector('input[name="EMAIL"]');
    if (!emailInput || !emailInput.checkValidity()) {
      this.#showStatus(this.dataset.i18nInvalid || '', 'error');
      emailInput?.focus();
      return;
    }

    const publicKey = this.dataset.klaviyoPublicKey?.trim();
    if (!publicKey) {
      this.#showStatus(this.dataset.i18nError || '', 'error');
      return;
    }

    const variantId =
      this.#host?.querySelector('[data-atc-variant-id]')?.value || this.#currentVariantId || '';
    // The subscription is attached to the variant - without one there is nothing to wait for,
    // so it is better to say so than to post a request Klaviyo will reject.
    if (!variantId) {
      this.#showStatus(this.dataset.i18nError || '', 'error');
      console.error('[notify-popup / Klaviyo] No variant id to subscribe to');
      return;
    }

    this.#setSubmitting(true);
    this.#hideStatus();

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const email = emailInput.value.trim();

    // The market goes on the profile first: the flow reads it to pick a language, and a
    // subscription that arrives before it would be handled with whatever the profile held
    // from an earlier visit. A failure here is logged, not fatal - being subscribed in the
    // wrong language beats not being subscribed at all.
    await this.#writeMarketToProfile(email, publicKey);

    const payload = this.#getKlaviyoPayload(email, variantId);
    const requestUrl = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(publicKey)}`;

    try {
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          // Same headers and revision the other COI themes send to this endpoint
          // (happy-ears, common-clouds, v-parts, switch-nails, wristbuddy, almada) - the
          // client APIs take plain JSON, and this revision is the one proven in production.
          'Content-Type': 'application/json',
          Accept: 'application/json',
          revision: '2024-06-15',
        },
        body: JSON.stringify(payload),
        signal: this.#abortController.signal,
      });

      const responseText = await response.text();
      const isAlreadySubscribed =
        response.status === 409 || this.#isAlreadySubscribed(responseText);

      if (!response.ok && !isAlreadySubscribed) {
        // An answer from Klaviyo is not a network failure. Saying "network error" for a
        // rejected subscription sent QA looking at connectivity while the real reason - an
        // unsynced catalog variant, a wrong company id - was sitting in the response body.
        console.error(
          `[notify-popup / Klaviyo] ${response.status} from back-in-stock-subscriptions:`,
          responseText
        );
        this.#showStatus(this.dataset.i18nError || '', 'error');
        return;
      }

      // Success and "already subscribed" are both wins for the visitor.
      this.#showSuccessState();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      // Only a request that never got an answer reaches here.
      this.#showStatus(this.dataset.i18nNetwork || this.dataset.i18nError || '', 'error');
      console.error('[notify-popup / Klaviyo] Request never completed', error);
    } finally {
      this.#setSubmitting(false);
    }
  };

  /**
   * Which storefront the request came from, written on the profile as its own properties.
   *
   * They are BIS-specific on purpose: the newsletter and footer forms write `Signup …` and a
   * later signup from another market would overwrite them, so a back-in-stock mail could go
   * out in the wrong language. `BIS Market` and `BIS Locale` are only ever written here, at
   * the moment the visitor asks about this product, and come from Liquid's view of the active
   * market - not from the visitor's IP.
   */
  async #writeMarketToProfile(email, publicKey) {
    const market = window.theme?.market ?? {};
    const marketCode = String(market.handle || market.countryCode || '')
      .trim()
      .toUpperCase();
    const language = String(market.languageCode || '').trim();
    const country = String(market.countryCode || '').trim().toUpperCase();
    const locale = language && country ? `${language}-${country}` : language;

    const properties = {};
    if (marketCode) properties['BIS Market'] = marketCode;
    if (locale) properties['BIS Locale'] = locale;
    if (!Object.keys(properties).length) return;

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/profiles/?company_id=${encodeURIComponent(publicKey)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            revision: '2024-06-15',
          },
          body: JSON.stringify({
            data: { type: 'profile', attributes: { email, properties } },
          }),
          signal: this.#abortController.signal,
        }
      );

      if (!response.ok) {
        console.error(
          `[notify-popup / Klaviyo] ${response.status} writing BIS market to profile:`,
          await response.text()
        );
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[notify-popup / Klaviyo] BIS market not written to profile', error);
    }
  }

  /**
   * A Back in Stock subscription, which is what Klaviyo's Back in Stock flow is triggered by.
   *
   * It used to be a list subscription (`/client/subscriptions` with a list relationship):
   * that added the visitor to a marketing list and recorded the variant as a plain profile
   * property, so Klaviyo had nothing to watch stock against and the flow could never fire.
   * Asking to be told about a product is also not consent to marketing, and this no longer
   * grants it.
   *
   * The variant is addressed by its id in Klaviyo's catalog. For the Shopify integration
   * that id is `$shopify:::$default:::<variant id>`, so the catalog has to be synced for the
   * subscription to be accepted.
   */
  #getKlaviyoPayload(email, variantId) {
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          // Only the email. Custom properties were sent here to read like the newsletter and
          // footer forms, and Klaviyo answered 400 "Invalid input" - this endpoint takes a
          // subscription, not a profile update. Every other COI theme posts the bare profile.
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
        },
        relationships: {
          variant: {
            data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` },
          },
        },
      },
    };
  }

  #isAlreadySubscribed(responseText = '') {
    if (!responseText) return false;
    const normalized = responseText.toLowerCase();
    return (
      normalized.includes('already subscribed') ||
      normalized.includes('already in list') ||
      normalized.includes('already exists') ||
      normalized.includes('duplicate') ||
      normalized.includes('already consented')
    );
  }

  #setSubmitting(on) {
    if (this.#submitBtn) {
      if (on) this.#submitBtn.setAttribute('disabled', '');
      else this.#submitBtn.removeAttribute('disabled');
    }
    if (this.#submitLabel) {
      this.#submitLabel.textContent = on
        ? (this.dataset.i18nSubmitting || '')
        : (this.dataset.i18nSubmit || '');
    }
  }

  #showStatus(text, type) {
    if (!this.#statusEl) return;
    this.#statusEl.textContent = text;
    this.#statusEl.dataset.type = type;
    this.#statusEl.removeAttribute('hidden');
  }

  #hideStatus() {
    if (!this.#statusEl) return;
    this.#statusEl.textContent = '';
    this.#statusEl.removeAttribute('data-type');
    this.#statusEl.setAttribute('hidden', '');
  }

  #showSuccessState() {
    this.dataset.state = 'success';
    if (this.#titleEl) this.#titleEl.textContent = this.dataset.i18nSuccess || this.#initialTitle;
    this.#form?.reset();
    this.#hideStatus();
  }

  #resetState() {
    delete this.dataset.state;
    if (this.#titleEl) this.#titleEl.textContent = this.#initialTitle;
  }
}

if (!customElements.get('notify-popup')) {
  customElements.define('notify-popup', NotifyPopup);
}
